/**
 * V2 Invoice Management entry point.
 *
 * Replaces the v1 InvoiceManagement/index.js when v2 is active.
 * - VM+ (internal) roles: Dashboard / Order Details / Invoice Details / Payments / Wallet
 * - Vendor role: Order Details / Invoice Details / Wallet
 *
 * Payments and Wallet each need their own permission AND their own feature
 * flag (`vip_payments` / `vip_wallet`), so the tab set is not a fixed count for
 * either audience.
 *
 * Order Details tab renders the existing v1 OrderDetails component.
 * Invoice Details tab renders the new v2 InvoiceList.
 * Dashboard tab renders the new v2 Dashboard (internal roles only).
 * Payments tab renders the Payments module (internal roles only).
 * Wallet tab renders the factory-level vendor wallet (ENG-24804); both internal
 * users and vendors see it, gated by `useWalletAccess` (flag + VENDOR_WALLET_VIEW).
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Box, styled, Tab, Tabs } from "@mui/material";

import OrderDetails from "../OrderDetails";
import InvoiceList from "./tabs/InvoiceList";
import Dashboard from "./tabs/Dashboard";
import Wallet from "./tabs/Wallet";
import PaymentList from "@/pages/Payments/PaymentList";
import { V2_COLORS } from "./constants/colors";
import { usePermissions } from "@/context/PermissionsContext";
import { BOUNCER_PERMISSIONS } from "@/constants/bouncerPermissions";
import { usePaymentFlag } from "@/pages/Payments/hooks/usePaymentFlag";
import { useWalletAccess } from "@/pages/Payments/hooks/useWalletAccess";
import { useRfqAccess } from "@/pages/Rfq/hooks/useRfqAccess";
import RfqList from "@/pages/Rfq/RfqList";
import { useIsMobile } from "@/hooks/useIsMobile";

const CustomTabs = styled(Tabs)({
  "& .MuiTabs-indicator": {
    backgroundColor: V2_COLORS.primary,
    height: "4px",
    borderTopLeftRadius: "2px",
    borderTopRightRadius: "2px",
  },
  "& .MuiTab-root.Mui-selected": {
    color: V2_COLORS.primary,
  },
  "& .MuiTab-root": {
    textTransform: "none",
  },
});

type TabKey = "dashboard" | "rfqs" | "orders" | "invoices" | "payments" | "wallet";

export default function V2InvoiceManagement() {
  const { hasPermission } = usePermissions();
  const isVendor = !hasPermission(BOUNCER_PERMISSIONS.ACCOUNTS_PAYABLE_VIEW);
  const isPaymentsEnabled = usePaymentFlag();
  // Internal users with the vip_payments flag and PAYMENT_VIEW get the Payments
  // tab, and it replaces the v1 Upload Payments button in Order Details.
  const canViewPayments =
    !isVendor && isPaymentsEnabled && hasPermission(BOUNCER_PERMISSIONS.PAYMENT_VIEW);
  // Shared with the header wallet icon — see useWalletAccess.
  const canViewWallet = useWalletAccess();
  const canViewRfqs = useRfqAccess();
  const isMobile = useIsMobile();
  const [searchParams, setSearchParams] = useSearchParams();

  const tabs = useMemo<{ key: TabKey; label: string }[]>(
    () =>
      isVendor
        ? [
            ...(canViewRfqs ? [{ key: "rfqs" as TabKey, label: "RFQs" }] : []),
            { key: "orders", label: "Order Details" },
            { key: "invoices", label: "Invoice Details" },
            ...(canViewWallet ? [{ key: "wallet" as TabKey, label: "Wallet" }] : []),
          ]
        : [
            { key: "dashboard", label: "Dashboard" },
            ...(canViewRfqs ? [{ key: "rfqs" as TabKey, label: "RFQs" }] : []),
            { key: "orders", label: "Order Details" },
            { key: "invoices", label: "Invoice Details" },
            ...(canViewPayments ? [{ key: "payments" as TabKey, label: "Payments" }] : []),
            ...(canViewWallet ? [{ key: "wallet" as TabKey, label: "Wallet" }] : []),
          ],
    [isVendor, canViewPayments, canViewWallet, canViewRfqs],
  );

  const defaultTab = isVendor ? "orders" : "dashboard";
  const tabParam = searchParams.get("tab") as TabKey | null;
  const activeTab = tabs.find((t) => t.key === tabParam)?.key ?? defaultTab;

  const [tabIndex, setTabIndex] = useState(() =>
    Math.max(0, tabs.findIndex((t) => t.key === activeTab))
  );

  useEffect(() => {
    const idx = tabs.findIndex((t) => t.key === activeTab);
    if (idx >= 0 && idx !== tabIndex) {
      setTabIndex(idx);
    }
  }, [activeTab, tabs, tabIndex]);

  const handleTabChange = (_: React.SyntheticEvent, newIndex: number) => {
    setTabIndex(newIndex);
    setSearchParams((prev) => {
      prev.set("tab", tabs[newIndex].key);
      prev.set("page", "1");
      prev.delete("status");
      prev.delete("filterData");
      return prev;
    });
  };

  const currentTab = activeTab;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: isMobile ? "calc(100vh - 48px)" : "calc(100vh - 64px)" }}>
      <CustomTabs
        value={tabIndex}
        onChange={handleTabChange}
        variant={isMobile ? "fullWidth" : "standard"}
        sx={{
          backgroundColor: "white",
          paddingLeft: isMobile ? 0 : "24px",
          flexShrink: 0,
          minHeight: isMobile ? "40px" : undefined,
          "& .MuiTab-root": {
            minHeight: isMobile ? "40px" : undefined,
            fontSize: isMobile ? "13px" : undefined,
            padding: isMobile ? "6px 12px" : undefined,
          },
        }}
      >
        {tabs.map((t) => (
          <Tab key={t.key} label={t.label} />
        ))}
      </CustomTabs>

      {currentTab === "dashboard" && <Dashboard />}
      {currentTab === "rfqs" && <RfqList />}
      {currentTab === "orders" && (
        <OrderDetails hideInvoiceUpload hidePaymentUpload={canViewPayments} hideInvoiceExpand />
      )}
      {/* Keep InvoiceList always mounted so batch SSE streams and modal state survive tab switches */}
      <Box sx={{ display: currentTab === "invoices" ? "flex" : "none", flexDirection: "column", flex: 1, minHeight: 0 }}>
        <InvoiceList />
      </Box>
      {currentTab === "payments" && <PaymentList />}
      {currentTab === "wallet" && <Wallet />}
    </Box>
  );
}
